import { computed } from 'vue';
import { ContainsTranslationProps } from '@/ui/components/activityLogs/types';
import {
  ActivityBasePayload,
  ActivityFieldUpdated,
  ActivityPayloadContent,
} from '@/entities/activity/model';

export function useActivityLog(
  field: string,
  fieldsTranslation: ContainsTranslationProps['fieldsTranslation'],
  valuesTranslation?: ContainsTranslationProps['valuesTranslation'],
) {
  const hasValueTranslation = computed(() => valuesTranslation ? !!valuesTranslation[field] : false);

  const fetchValueTranslation = (value: string) => valuesTranslation![field](value);

  const isMultipleItems = (payload: Array<unknown> | unknown) => Array.isArray(payload);

  const isSettingFieldsForFirstTime = (payload: ActivityBasePayload): boolean => {
    if (isMultipleItems(payload.old)) {
      return (payload.old as Array<unknown>).length < 1;
    }

    return (payload.old as ActivityPayloadContent).value === null;
  };

  const hasNewValue = (payload: ActivityBasePayload): boolean => {
    if (isMultipleItems(payload.new)) {
      return (payload.new as Array<unknown>).length > 0;
    }

    return (payload.new as ActivityPayloadContent).value !== null;
  };

  const fieldTranslation = computed(() => {
    if (fieldsTranslation[field] === undefined) {
      console.error(`Translation for field ${field} is missing`);

      return field;
    }

    return fieldsTranslation[field];
  });

  const valuesToText = (record: ActivityFieldUpdated, timeline: 'new' | 'old') => {
    if (record.payload === null) {
      return '';
    }

    if (isMultipleItems(record.payload[timeline])) {
      return (record.payload[timeline] as ActivityPayloadContent[])
        .map(
          n => hasValueTranslation.value
            ? fetchValueTranslation((n.value as (number | string)).toString())
            : n.value,
        )
        .join(', ');
    }

    return hasValueTranslation.value
      ? fetchValueTranslation((
        (record.payload[timeline] as ActivityPayloadContent).value as (string | number)).toString())
      : (record.payload[timeline] as ActivityPayloadContent).value as (string | null);
  };

  return {
    hasValueTranslation,
    fetchValueTranslation,
    isSettingFieldsForFirstTime,
    fieldTranslation,
    isMultipleItems,
    valuesToText,
    hasNewValue,
  };
}
